<!-- 德育宠物配置。最后更新：2026-10-07 -->
<template>
  <section class="dp-layout">
    <section class="dp-left">
      <DepartmentPane directory-scope="school" @tree-node-click="treeNodeClick" :type="1"></DepartmentPane>
    </section>
    <section class="dp-right pet-config-page" v-loading="loading">
      <div class="page-head">
        <p class="dp-dept-hint">
          <span class="dp-dept-hint__label">当前学校</span>
          <span class="dp-dept-hint__value">{{ schoolName || '未选择' }}</span>
        </p>
        <div class="page-head__actions">
          <el-button size="small" icon="el-icon-time" :disabled="!schoolId" @click="openLogs">变更记录</el-button>
          <el-button size="small" icon="el-icon-refresh" :disabled="!schoolId" @click="load">重新加载</el-button>
          <el-button type="primary" size="small" icon="el-icon-check" :loading="saving" :disabled="!schoolId" @click="save">保存配置</el-button>
        </div>
      </div>

      <div v-if="!schoolId" class="empty-state">
        <i class="el-icon-school"></i>
        <span>请先在顶部切换到具体学校</span>
      </div>

      <div v-else class="page-body">
        <div class="stat-strip">
          <div class="stat-card">
            <span class="stat-card__label">功能状态</span>
            <span class="stat-card__value stat-card__value--text">{{ config.enabled ? '已开启' : '已关闭' }}</span>
          </div>
          <div class="stat-card">
            <span class="stat-card__label">等级数量</span>
            <span class="stat-card__value">{{ config.levels.length }}<small>级</small></span>
          </div>
          <div class="stat-card">
            <span class="stat-card__label">学期分模式</span>
            <span class="stat-card__value stat-card__value--text">{{ config.scoreMode === 'PositiveOnly' ? '只计加分' : '净分' }}</span>
          </div>
          <div class="stat-card">
            <span class="stat-card__label">家长每学期可更换</span>
            <span class="stat-card__value">
              <template v-if="config.maxChangePerSemester < 0">不限</template>
              <template v-else>{{ config.maxChangePerSemester }}<small>次</small></template>
            </span>
          </div>
          <div class="stat-card">
            <span class="stat-card__label">最高等级门槛</span>
            <span class="stat-card__value">{{ topMinScore }}<small>分</small></span>
          </div>
        </div>

        <div class="config-grid">
          <div class="panel panel--rule">
            <div class="panel__head">
              <span class="panel__title">基础规则</span>
              <span class="panel__desc">影响家长端等级计算与更换限制</span>
            </div>
            <el-form label-position="top" class="rule-form panel__scroll">
              <el-form-item label="宠物功能">
                <div class="setting-list">
                  <div class="setting-row">
                    <div class="setting-row__text">
                      <span class="setting-row__title">开启学校宠物功能</span>
                      <span class="setting-row__desc">关闭后家长端和班主任端不可用，学生评价统一显示学生头像；后台仍可配置与上传，便于再次开启</span>
                    </div>
                    <el-switch v-model="config.enabled" />
                  </div>
                </div>
              </el-form-item>
              <el-form-item label="等级数量">
                <el-radio-group v-model="levelCount" size="small" @change="resetLevels">
                  <el-radio-button v-for="n in [6, 8, 10]" :key="n" :label="n">{{ n }} 级</el-radio-button>
                </el-radio-group>
                <div class="field-tip">切换后保留已填写的前几级，新增等级按每级 100 分递增预填。</div>
              </el-form-item>
              <el-form-item label="学期分计算方式">
                <div class="option-cards">
                  <div
                    v-for="item in scoreModes"
                    :key="item.value"
                    :class="['option-card', { 'is-active': config.scoreMode === item.value }]"
                    @click="config.scoreMode = item.value">
                    <span class="option-card__title">{{ item.label }}</span>
                    <span class="option-card__desc">{{ item.desc }}</span>
                  </div>
                </div>
              </el-form-item>
              <el-form-item label="家长每学期最多更换次数">
                <div class="inline-field">
                  <el-input-number v-model="config.maxChangePerSemester" :min="-1" :max="99" size="small" controls-position="right" />
                  <el-checkbox :value="config.maxChangePerSemester < 0" @change="toggleUnlimited">不限次数</el-checkbox>
                </div>
                <div class="field-tip">-1 表示不限；管理员和班主任代更换不占用家长次数。</div>
              </el-form-item>
              <el-form-item label="学生评价头像">
                <div class="option-cards">
                  <div
                    v-for="item in avatarSources"
                    :key="item.value"
                    :class="['option-card', { 'is-active': config.evaluationAvatarSource === item.value }]"
                    @click="config.evaluationAvatarSource = item.value">
                    <span class="option-card__title">{{ item.label }}</span>
                    <span class="option-card__desc">{{ item.desc }}</span>
                  </div>
                </div>
              </el-form-item>
              <el-form-item label="班主任代操作">
                <div class="setting-list">
                  <div class="setting-row">
                    <div class="setting-row__text">
                      <span class="setting-row__title">允许班主任代领取</span>
                      <span class="setting-row__desc">班主任可为本人负责班级未领取的学生选择宠物</span>
                    </div>
                    <el-switch v-model="config.allowClassTeacherClaim" />
                  </div>
                  <div class="setting-row">
                    <div class="setting-row__text">
                      <span class="setting-row__title">允许班主任代更换</span>
                      <span class="setting-row__desc">仅限当前学期，不占用家长更换次数</span>
                    </div>
                    <el-switch v-model="config.allowClassTeacherChange" />
                  </div>
                </div>
              </el-form-item>
            </el-form>
          </div>

          <div class="panel panel--grow">
            <div class="panel__head">
              <span class="panel__title">等级门槛</span>
              <span class="panel__desc">学期分达到最低分即进入该等级，门槛需逐级递增</span>
            </div>
            <div class="level-table-wrap">
            <el-table :data="config.levels" class="level-table" size="small" height="100%">
              <el-table-column label="等级" width="80" align="center">
                <template slot-scope="{ row }">
                  <span class="level-badge" :style="levelBadgeStyle(row.levelIndex)">Lv.{{ row.levelIndex }}</span>
                </template>
              </el-table-column>
              <el-table-column label="等级名称" min-width="160">
                <template slot-scope="{ row }">
                  <el-input v-model="row.levelName" size="small" maxlength="20" placeholder="如：萌芽期" />
                </template>
              </el-table-column>
              <el-table-column label="最低学期分" width="180">
                <template slot-scope="{ row, $index }">
                  <el-input-number
                    v-model="row.minScore"
                    size="small"
                    :min="0"
                    :disabled="$index === 0"
                    controls-position="right"
                    :class="{ 'is-invalid': invalidIndexes.includes($index) }" />
                </template>
              </el-table-column>
              <el-table-column label="分数区间" min-width="200">
                <template slot-scope="{ row, $index }">
                  <div class="range-cell">
                    <div class="range-bar"><span :style="{ width: rangePercent(row) + '%' }"></span></div>
                    <span class="range-text">{{ rangeText(row, $index) }}</span>
                  </div>
                </template>
              </el-table-column>
            </el-table>
            </div>
            <div v-if="invalidIndexes.length" class="level-warning">
              <i class="el-icon-warning-outline"></i>
              第 {{ invalidIndexes.map(i => i + 1).join('、') }} 级的最低分未高于上一级，请调整后再保存。
            </div>
          </div>
        </div>
      </div>

      <el-dialog title="配置变更记录" :visible.sync="logVisible" width="760px" append-to-body>
        <el-table :data="logs" v-loading="logLoading" size="small" :max-height="logTableMaxHeight" class="log-table">
          <el-table-column label="修改时间" width="150" align="center">
            <template slot-scope="{ row }"><span class="log-time">{{ formatTime(row.createTime) }}</span></template>
          </el-table-column>
          <el-table-column prop="operatorName" label="操作人" width="110" align="center" show-overflow-tooltip />
          <el-table-column label="修改内容" min-width="380">
            <template slot-scope="{ row }">
              <ul class="log-changes">
                <li v-for="(text, i) in row.changes" :key="i">{{ text }}</li>
              </ul>
            </template>
          </el-table-column>
        </el-table>
        <div class="log-actions">
          <el-button type="warning" plain size="small" icon="el-icon-download" :loading="logExporting" @click="exportLogs">导出全部记录</el-button>
        </div>
        <div class="log-pager">
          <el-pagination
            small
            layout="total, prev, pager, next"
            :current-page="logPage"
            :page-size="logPageSize"
            :total="logTotal"
            @current-change="loadLogs" />
        </div>
      </el-dialog>
    </section>
  </section>
</template>

<script>
import DepartmentPane from '@/components/DepartmentPane.vue'
import { getPetConfig, savePetConfig, getPetConfigChangeLogList, exportPetConfigChangeLogList } from '@/api/modules/pet'
import { treeNodeDeptId } from '@/utils/deptCascaderProps'

const levelColors = ['#64748b', '#0891b2', '#0d9488', '#16a34a', '#65a30d', '#ca8a04', '#ea580c', '#dc2626', '#c026d3', '#7c3aed']

export default {
  name: 'PetConfig',
  components: { DepartmentPane },
  data () {
    return {
      schoolId: 0,
      schoolName: '',
      loading: false,
      saving: false,
      levelCount: 10,
      config: this.buildDefaultConfig(10),
      originalConfig: null,
      logVisible: false,
      logLoading: false,
      logExporting: false,
      logs: [],
      logPage: 1,
      logPageSize: 10,
      logTotal: 0,
      logTableMaxHeight: 460,
      scoreModes: [
        { value: 'Net', label: '净分', desc: '加分与扣分相抵后累计' },
        { value: 'PositiveOnly', label: '只计加分', desc: '扣分不影响宠物等级，当天状态仍按净分判断' }
      ],
      avatarSources: [
        { value: 'Student', label: '学生头像', desc: '显示学生本人头像，宠物只在成长详情中展示' },
        { value: 'Pet', label: '宠物头像', desc: '显示学生当前宠物的等级头像，未领取时显示学生头像' }
      ]
    }
  },
  computed: {
    topMinScore () {
      const levels = this.config.levels || []
      return levels.length ? Number(levels[levels.length - 1].minScore) || 0 : 0
    },
    invalidIndexes () {
      const levels = this.config.levels || []
      return levels.reduce((result, item, index) => {
        if (index > 0 && Number(item.minScore) <= Number(levels[index - 1].minScore)) result.push(index)
        return result
      }, [])
    }
  },
  methods: {
    /** 构造默认等级配置 */
    buildDefaultConfig (count) {
      return {
        version: 1,
        enabled: false,
        scoreMode: 'Net',
        evaluationAvatarSource: 'Student',
        maxChangePerSemester: 2,
        allowClassTeacherClaim: false,
        allowClassTeacherChange: false,
        levels: Array.from({ length: count }, (_, i) => ({ levelIndex: i + 1, levelName: `第${i + 1}级`, minScore: i * 100 }))
      }
    },
    /** 学校切换（顶栏驱动） */
    treeNodeClick (data) {
      this.schoolId = treeNodeDeptId(data)
      this.schoolName = (data && data.label) || this.$store.state.activeSchoolName || ''
      if (this.schoolId) this.load()
    },
    async load () {
      if (!this.schoolId) return
      this.loading = true
      try {
        const res = await getPetConfig({ schoolId: this.schoolId })
        const data = res.data && res.data.response
        if (data && Array.isArray(data.levels) && data.levels.length) {
          this.config = Object.assign(this.buildDefaultConfig(data.levels.length), data)
          this.originalConfig = JSON.parse(JSON.stringify(this.config))
          this.levelCount = data.levels.length
        } else {
          this.config = this.buildDefaultConfig(10)
          this.originalConfig = JSON.parse(JSON.stringify(this.config))
          this.levelCount = 10
        }
      } finally {
        this.loading = false
      }
    },
    resetLevels () {
      const old = this.config.levels || []
      this.config.levels = Array.from({ length: this.levelCount }, (_, i) => old[i]
        ? Object.assign({}, old[i], { levelIndex: i + 1 })
        : { levelIndex: i + 1, levelName: `第${i + 1}级`, minScore: i * 100 })
    },
    toggleUnlimited (checked) {
      this.config.maxChangePerSemester = checked ? -1 : 2
    },
    levelBadgeStyle (levelIndex) {
      const total = (this.config.levels || []).length || 10
      const colorIndex = Math.min(levelColors.length - 1, Math.round(((levelIndex - 1) / Math.max(1, total - 1)) * (levelColors.length - 1)))
      const color = levelColors[colorIndex]
      return { color, background: `${color}14`, borderColor: `${color}55` }
    },
    rangePercent (row) {
      const top = this.topMinScore || 1
      return Math.min(100, Math.max(4, (Number(row.minScore) || 0) / top * 100))
    },
    rangeText (row, index) {
      const next = this.config.levels[index + 1]
      if (!next) return `${row.minScore} 分及以上`
      return `${row.minScore} ~ ${Math.max(row.minScore, Number(next.minScore) - 1)} 分`
    },
    async save () {
      if (!this.schoolId) return this.$message.warning('请先在顶部切换学校')
      if (this.config.levels.some(x => !String(x.levelName || '').trim())) return this.$message.warning('等级名称不能为空')
      if (this.invalidIndexes.length) return this.$message.warning('等级最低分需逐级递增')
      const oldConfig = this.originalConfig || {}
      const impactChanged = !this.originalConfig
        || oldConfig.enabled !== this.config.enabled
        || oldConfig.scoreMode !== this.config.scoreMode
        || oldConfig.evaluationAvatarSource !== this.config.evaluationAvatarSource
        || oldConfig.maxChangePerSemester !== this.config.maxChangePerSemester
        || oldConfig.allowClassTeacherClaim !== this.config.allowClassTeacherClaim
        || oldConfig.allowClassTeacherChange !== this.config.allowClassTeacherChange
        || JSON.stringify(oldConfig.levels || []) !== JSON.stringify(this.config.levels || [])
      if (impactChanged) {
        try {
          await this.$confirm(
            '修改功能开关、等级、阈值、计分方式、评价头像、更换次数或班主任代操作权限后，会实时影响全校学生，是否继续？',
            '确认保存宠物配置',
            { type: 'warning', confirmButtonText: '继续保存', cancelButtonText: '取消' }
          )
        } catch (e) {
          return
        }
      }
      this.saving = true
      try {
        const res = await savePetConfig({ departmentId: this.schoolId, config: this.config })
        if (res.data && res.data.success) {
          this.originalConfig = JSON.parse(JSON.stringify(this.config))
          this.$message.success('保存成功')
        }
        else this.$message.error((res.data && res.data.msg) || '保存失败')
      } finally {
        this.saving = false
      }
    },
    openLogs () {
      this.logTableMaxHeight = Math.max(240, Math.min(460, Math.floor(window.innerHeight * 0.88) - 230))
      this.logVisible = true
      this.loadLogs(1)
    },
    /** 分页加载配置变更记录 */
    async loadLogs (page) {
      this.logPage = page || 1
      this.logLoading = true
      try {
        const res = await getPetConfigChangeLogList({ schoolId: this.schoolId, page: this.logPage, pageSize: this.logPageSize })
        const data = (res.data && res.data.response) || {}
        this.logs = data.data || []
        this.logTotal = data.dataCount || 0
      } finally {
        this.logLoading = false
      }
    },
    /** 导出学校全部配置变更记录。 */
    async exportLogs () {
      this.logExporting = true
      try {
        const res = await exportPetConfigChangeLogList({ schoolId: this.schoolId })
        if (!res.data || !res.data.size) return this.$message.error('没有可导出的记录')
        const url = window.URL.createObjectURL(new Blob([res.data]))
        const link = document.createElement('a')
        link.href = url
        link.download = `宠物配置变更记录_${this.schoolId}.xlsx`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        this.$message.error('导出失败')
      } finally {
        this.logExporting = false
      }
    },
    formatTime (value) {
      if (!value) return '—'
      return String(value).replace('T', ' ').slice(0, 16)
    }
  }
}
</script>

<style scoped>
.pet-config-page { display: flex; flex-direction: column; min-height: 0; }
.page-head { flex: 0 0 auto; display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; margin-bottom: 12px; }
.page-head .dp-dept-hint { margin: 0; }
.page-head__actions { display: flex; gap: 8px; }
.log-time { font-size: 12px; color: #475569; font-variant-numeric: tabular-nums; }
.log-changes { margin: 0; padding-left: 16px; font-size: 13px; color: #0f172a; line-height: 1.7; }
.log-pager { display: flex; justify-content: flex-end; margin-top: 10px; }
.log-actions { display: flex; justify-content: flex-start; margin-top: 10px; }
.page-body { flex: 1 1 auto; min-height: 0; overflow: hidden; display: flex; flex-direction: column; gap: 14px; }
.page-body > .stat-strip { flex-shrink: 0; }

.empty-state { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; color: #64748b; min-height: 320px; }
.empty-state i { font-size: 40px; color: #94a3b8; }

.stat-strip { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 12px; }
.stat-card { display: flex; flex-direction: column; gap: 6px; padding: 14px 16px; background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; border-left: 3px solid #2563eb; }
.stat-card:nth-child(2) { border-left-color: #0d9488; }
.stat-card:nth-child(3) { border-left-color: #f59e0b; }
.stat-card:nth-child(4) { border-left-color: #7c3aed; }
.stat-card:nth-child(5) { border-left-color: #0284c7; }
.stat-card__label { font-size: 12px; color: #64748b; }
.stat-card__value { font-size: 22px; font-weight: 600; color: #0f172a; line-height: 1.2; }
.stat-card__value small { font-size: 12px; font-weight: 400; color: #64748b; margin-left: 2px; }
.stat-card__value--text { font-size: 18px; }

.config-grid { flex: 1 1 auto; min-height: 0; display: grid; grid-template-columns: minmax(320px, 400px) minmax(0, 1fr); gap: 14px; align-items: stretch; }
.panel { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 16px 18px; min-width: 0; }
.config-grid > .panel { display: flex; flex-direction: column; min-height: 0; overflow: hidden; }
.panel__scroll { flex: 1 1 auto; min-height: 0; overflow-y: auto; margin-right: -10px; padding-right: 10px; }
.level-table-wrap { flex: 1 1 auto; min-height: 0; }
.panel__head { flex-shrink: 0; display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; padding-bottom: 12px; margin-bottom: 12px; border-bottom: 1px solid #f1f5f9; }
.panel__title { font-size: 15px; font-weight: 600; color: #0f172a; }
.panel__desc { font-size: 12px; color: #64748b; }

.rule-form /deep/ .el-form-item { margin-bottom: 18px; }
.rule-form /deep/ .el-form-item__label { padding-bottom: 6px; line-height: 20px; font-weight: 500; color: #334155; }
.field-tip { margin-top: 6px; font-size: 12px; line-height: 1.5; color: #64748b; }
.inline-field { display: flex; align-items: center; gap: 14px; }
.setting-list { width: 100%; line-height: normal; border: 1px solid #e2e8f0; border-radius: 10px; overflow: hidden; }
.setting-row { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 12px 14px; background: #fff; }
.setting-row + .setting-row { border-top: 1px solid #f1f5f9; }
.setting-row:hover { background: #f8fafc; }
.setting-row__text { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.setting-row__title { font-size: 14px; font-weight: 500; line-height: 1.4; color: #0f172a; }
.setting-row__desc { font-size: 12px; line-height: 1.5; color: #64748b; }
.setting-row .el-switch { flex-shrink: 0; }

.option-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; width: 100%; }
.option-card { display: flex; flex-direction: column; gap: 4px; padding: 10px 12px; border: 1px solid #e2e8f0; border-radius: 8px; cursor: pointer; transition: border-color .2s ease, background-color .2s ease; line-height: 1.4; }
.option-card:hover { border-color: #93c5fd; }
.option-card.is-active { border-color: #2563eb; background: #eff6ff; }
.option-card__title { font-size: 13px; font-weight: 600; color: #0f172a; }
.option-card.is-active .option-card__title { color: #1d4ed8; }
.option-card__desc { font-size: 12px; color: #64748b; }

.level-table { width: 100%; }
.level-table /deep/ .el-input-number { width: 150px; }
.level-table /deep/ .is-invalid .el-input__inner { border-color: #dc2626; }
.level-badge { display: inline-block; min-width: 46px; padding: 2px 8px; border: 1px solid; border-radius: 999px; font-size: 12px; font-weight: 600; text-align: center; }
.range-cell { display: flex; align-items: center; gap: 10px; }
.range-bar { flex: 1; height: 6px; border-radius: 999px; background: #f1f5f9; overflow: hidden; }
.range-bar span { display: block; height: 100%; border-radius: 999px; background: linear-gradient(90deg, #60a5fa, #2563eb); transition: width .25s ease; }
.range-text { flex-shrink: 0; min-width: 110px; font-size: 12px; color: #475569; }
.level-warning { flex-shrink: 0; margin-top: 10px; padding: 8px 12px; border-radius: 8px; background: #fef2f2; color: #b91c1c; font-size: 12px; }

@media (max-width: 1280px) {
  .page-body { overflow-y: auto; }
  .config-grid { flex: none; grid-template-columns: 1fr; }
  .config-grid > .panel { overflow: visible; }
  .panel__scroll { overflow: visible; margin-right: 0; padding-right: 0; }
  .level-table-wrap { flex: none; height: 460px; }
  .stat-strip { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
@media (max-width: 900px) {
  .stat-strip { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
</style>
